import { mutate } from 'swr';

import { Collateral, CollateralPayload } from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';

import type { Collateral as CollateralDocument } from '@/payload-types';

import { PayloadSDK } from './PayloadSDK';

const CollateralService = {
    mutate: {
        list: (companyId: string) => mutate(CollateralHelpers.swr.list(companyId)),
    },

    list: async (companyId: string) => {
        const { docs } = await PayloadSDK.find({
            collection: 'collateral',
            where: { company: { equals: companyId } },
            sort: 'createdAt',
            pagination: false,
            depth: 1,
        });

        return docs as unknown as Collateral[];
    },

    listByIds: async (ids: string[]) => {
        const { docs } = await PayloadSDK.find({
            collection: 'collateral',
            where: { id: { in: ids } },
            pagination: false,
            depth: 0,
        });

        return docs as unknown as Collateral[];
    },

    listByProduct: async (productId: string) => {
        const { docs } = await PayloadSDK.find({
            collection: 'collateral',
            where: { product: { equals: productId } },
            pagination: false,
            depth: 1,
        });

        return docs as unknown as Collateral[];
    },

    get: async (id: string) => {
        const doc = await PayloadSDK.findByID({ collection: 'collateral', id, depth: 1, disableErrors: true });

        return doc as unknown as Collateral | null;
    },

    create: async (data: CollateralPayload) => {
        const doc = await PayloadSDK.create({
            collection: 'collateral',
            // The zod payload allows every ComponentFileType; the collection only accepts the collateral subset.
            data: data as unknown as CollateralDocument,
        });

        return doc as unknown as Collateral;
    },

    update: async (id: string, data: Partial<CollateralPayload>) => {
        const doc = await PayloadSDK.update({
            collection: 'collateral',
            id,
            data: data as unknown as Partial<CollateralDocument>,
        });

        return doc as unknown as Collateral;
    },

    delete: async (id: string) => {
        await PayloadSDK.delete({ collection: 'collateral', id });
    },
};

export { CollateralService };
